import { TFunction } from 'i18next';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { isDefined } from '../../../../utils/variables';
import { useDialog } from '../../../hooks/use-dialog';
import { CheckBox } from '../../check-box/check-box';
import { DataTable, DataTableColumns, DataTableSortType } from '../../data-table/data-table';
import { FileSelectionIcon } from '../../file-selection-icon/file-selection-icon';
import { Icon } from '../../icon/icon';
import { IconPanel } from '../../icon-panel/icon-panel';
import { OverlayLoader } from '../../overlay-loader/overlay-loader';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';
import { GeodataImageItemWithMarkers, PlaceMarkersContext } from '../gcp-page/place-markers-state';
import { GeodataImagesContext } from '../geodata-images-state';
import { GeodataContext } from '../geodata-state';
import { BulkUpdateImagesDialog } from './bulk-update-images-dialog';

const pageSize = 15;

const searchKeys = ['displayName', 'status'] as const;

function filterItems(
  items: GeodataImageItemWithMarkers[],
  searchText: string,
): GeodataImageItemWithMarkers[] {
  const text = searchText.trim().toLowerCase();
  if (text.length === 0) {
    return items;
  }

  return items.filter((item) =>
    searchKeys.some((x) => item[x]?.toLocaleLowerCase().includes(text)),
  );
}

const NoImagesPanel = () => {
  const { t } = useTranslation();
  return (
    <NoImagesPanelStyle>
      <IconPanel
        header={{ icon: { icon: ['fad', 'magnifying-glass'], opacity: '50%', size: '4x' } }}
        responsiveness={{ compressed: false }}
      >
        {{
          title: t('imageList.noImagesTitle', { ns: 'cloudProcessing' }),
          info: t('imageList.noImagesInfo', { ns: 'cloudProcessing' }),
        }}
      </IconPanel>
    </NoImagesPanelStyle>
  );
};

const NoImagesPanelStyle = styled.div`
  display: flex;
  padding: 1em;
`;

const getColumns = (
  allSelected: boolean,
  onToggleAll: () => void,
  selectedImageIds: Set<string>,
  toggleItemSelection: (item: GeodataImageItemWithMarkers) => void,
  t: TFunction,
): DataTableColumns<GeodataImageItemWithMarkers> => ({
  checkbox: {
    title: <CheckBox checked={allSelected} expandClickableArea={true} onChange={onToggleAll} />,
    alignment: 'center',
    alignColumnContent: 'center',
    width: '25px',
    padding: 0,
    unfilterable: true,
    render: (item: GeodataImageItemWithMarkers) => (
      <CheckBox
        checked={item.imageId ? selectedImageIds.has(item.imageId) : false}
        onChange={() => toggleItemSelection(item)}
      />
    ),
  },
  statusIcon: {
    title: t('aligner.table.status', { ns: 'cloudProcessing' }),
    alignment: 'center',
    width: '25px',
    padding: 0,
    sortableBy: { type: DataTableSortType.STRING, columnKey: 'status' },
    unfilterable: true,
  },
  useGpsIcon: {
    title: 'GPS',
    alignment: 'center',
    width: '25px',
    padding: 0,
    sortableBy: { type: DataTableSortType.BOOLEAN, columnKey: 'useGps' },
    unfilterable: true,
  },
  markerStatusIcon: {
    title: t('markerList.title', { ns: 'cloudProcessing' }),
    alignment: 'center',
    width: '25px',
    padding: 0,
    sortableBy: { type: DataTableSortType.STRING, columnKey: 'markerStatus' },
    unfilterable: true,
  },
  displayName: {
    sortableBy: { type: DataTableSortType.STRING },
    unfilterable: true,
    title: t('name', { ns: 'common' }),
  },
  totalStandardDeviation: {
    sortableBy: { type: DataTableSortType.NUMBER },
    unfilterable: true,
    title: t('aligner.table.standardDeviation', { ns: 'cloudProcessing' }),
    width: '75px',
    formatter(value) {
      return value.toFixed(3);
    },
  },
});

const tableStyle = {
  rowCell: {
    fontSize: '14px',
  },
  headerCell: {
    fontSize: '14px',
  },
};

/**
 * Lists the images related to the Geodata with the option to upload or remove existing images.
 * Adding or removing images will be disabled when the process has failed or finished.
 */
export function GeodataImageList() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { isGeodataProcessing, geodata } = useContext(GeodataContext);
  const { isPendingGetImages, uploadImage, linkLocalImages, refetchImages } =
    useContext(GeodataImagesContext);
  const { setShowPlaceMarkersDialog, image, selectImage, imagesWithMarkers, setSortedImageList } =
    useContext(PlaceMarkersContext);
  const [filteredItems, setFilteredItems] = useState<GeodataImageItemWithMarkers[]>([]);
  const [sortedItems, setSortedItems] = useState<GeodataImageItemWithMarkers[]>([]);
  const [searchText, setSearchText] = useState('');
  const [selectedImageIds, setSelectedImageIds] = useState<Set<string>>(new Set());
  const bulkUpdateDialog = useDialog();
  const ctrlKeyPressedRef = useRef(false);
  const shiftKeyPressedRef = useRef(false);
  const lastSelectedImageIdRef = useRef<string | null>(null);
  // Lets `toggleItemSelection`/`onRowClicked` read the latest sorted order without depending on
  // `sortedItems` directly: depending on it recreates those callbacks (and therefore the
  // DataTable `columns` object that embeds them) every time the table reports a new sort, which
  // feeds straight back into DataTable's own sort recomputation and loops forever.
  const sortedItemsRef = useRef(sortedItems);
  const renderNoImagesPanel = !isPendingGetImages && imagesWithMarkers.length === 0;
  const isMultiselectActive = useMemo(() => selectedImageIds.size > 0, [selectedImageIds]);

  useEffect(() => {
    sortedItemsRef.current = sortedItems;
  }, [sortedItems]);

  useEffect(() => {
    setFilteredItems(filterItems(imagesWithMarkers, searchText));
  }, [imagesWithMarkers, searchText]);

  // Track Ctrl and Shift key state
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Control' || e.key === 'Meta') {
        ctrlKeyPressedRef.current = true;
      }
      if (e.key === 'Shift') {
        shiftKeyPressedRef.current = true;
      }
      // Handle Ctrl+A (or Cmd+A on Mac)
      if ((e.ctrlKey || e.metaKey) && (e.key === 'a' || e.key === 'A')) {
        e.preventDefault();
        if (filteredItems.length > 0) {
          const itemsWithIds = filteredItems.filter((item) => isDefined(item.imageId));
          const allImageIds = new Set(itemsWithIds.map((item) => item.imageId!));
          setSelectedImageIds(allImageIds);
          // Set last selected to the last item in the filtered list
          if (itemsWithIds.length > 0) {
            const lastItem = itemsWithIds[itemsWithIds.length - 1];
            if (lastItem?.imageId) {
              lastSelectedImageIdRef.current = lastItem.imageId;
            }
          }
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.key === 'Control' || e.key === 'Meta') {
        ctrlKeyPressedRef.current = false;
      }
      if (e.key === 'Shift') {
        shiftKeyPressedRef.current = false;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [filteredItems]);

  const highlightRow = useCallback(
    (item: GeodataImageItemWithMarkers) => {
      return item.imageId === image?.imageId;
    },
    [image],
  );

  const handleBulkUpdate = useCallback(() => {
    bulkUpdateDialog.show();
  }, [bulkUpdateDialog]);

  const selectedItems = useMemo(() => {
    // Use sortedItems if available (when sorting is active), otherwise use filteredItems
    const itemsToSearch = sortedItems.length > 0 ? sortedItems : filteredItems;
    return itemsToSearch.filter((item) => item.imageId && selectedImageIds.has(item.imageId));
  }, [sortedItems, filteredItems, selectedImageIds]);

  const toggleItemSelection = useCallback(
    (item: GeodataImageItemWithMarkers) => {
      if (!item.imageId) {
        return;
      }

      const shiftPressed = shiftKeyPressedRef.current;

      // Handle shift-click for range selection
      if (shiftPressed && lastSelectedImageIdRef.current) {
        const lastSelectedId = lastSelectedImageIdRef.current;
        const clickedId = item.imageId;

        // Use sortedItems if available (when sorting is active), otherwise use filteredItems
        const itemsToSearch =
          sortedItemsRef.current.length > 0 ? sortedItemsRef.current : filteredItems;

        // Find indices of last selected and clicked items in the sorted/filtered order
        const lastSelectedIndex = itemsToSearch.findIndex((i) => i.imageId === lastSelectedId);
        const clickedIndex = itemsToSearch.findIndex((i) => i.imageId === clickedId);

        if (lastSelectedIndex !== -1 && clickedIndex !== -1) {
          // Determine range (from smaller index to larger index)
          const startIndex = Math.min(lastSelectedIndex, clickedIndex);
          const endIndex = Math.max(lastSelectedIndex, clickedIndex);

          // Select all items in the range
          setSelectedImageIds((prev) => {
            const newSet = new Set(prev);
            for (let i = startIndex; i <= endIndex; i++) {
              const rangeItem = itemsToSearch[i];
              if (rangeItem?.imageId) {
                newSet.add(rangeItem.imageId);
              }
            }
            return newSet;
          });

          // Update last selected to the clicked item
          lastSelectedImageIdRef.current = clickedId;
          return;
        }
      }

      // Normal toggle behavior
      setSelectedImageIds((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(item.imageId!)) {
          newSet.delete(item.imageId!);
        } else {
          newSet.add(item.imageId!);
        }
        return newSet;
      });
      // Update last selected item
      lastSelectedImageIdRef.current = item.imageId;
    },
    [filteredItems],
  );

  const onRowClicked = useCallback(
    async (item: GeodataImageItemWithMarkers) => {
      const ctrlPressed = ctrlKeyPressedRef.current;
      const shiftPressed = shiftKeyPressedRef.current;

      if (ctrlPressed || shiftPressed) {
        return toggleItemSelection(item);
      }

      // Normal click behavior
      if (item.status === 'UPLOAD_FAILED' && item.file) {
        await uploadImage(item.file);
        // Update last selected item
        if (item.imageId) {
          lastSelectedImageIdRef.current = item.imageId;
        }
      } else if (item.status === 'COMPLETED') {
        setSortedImageList(
          sortedItemsRef.current
            .filter((x) => isDefined(x.imageId))
            .map((x) => {
              return {
                imageId: x.imageId!,
              };
            }),
        );
        selectImage(item);
        setShowPlaceMarkersDialog(true);
        // Update last selected item
        if (item.imageId) {
          lastSelectedImageIdRef.current = item.imageId;
        }
      }
    },
    [uploadImage, selectImage, setShowPlaceMarkersDialog, setSortedImageList, toggleItemSelection],
  );

  const allItemsSelected = useMemo(() => {
    const itemsWithIds = filteredItems.filter((item) => isDefined(item.imageId));
    return (
      itemsWithIds.length > 0 &&
      itemsWithIds.every((item) => item.imageId && selectedImageIds.has(item.imageId))
    );
  }, [filteredItems, selectedImageIds]);

  const handleToggleAll = useCallback(() => {
    const itemsWithIds = filteredItems.filter((item) => isDefined(item.imageId));
    const filteredImageIds = new Set(itemsWithIds.map((item) => item.imageId!));

    if (allItemsSelected) {
      // Deselect only filtered items
      setSelectedImageIds((prev) => {
        const newSet = new Set(prev);
        filteredImageIds.forEach((id) => newSet.delete(id));
        return newSet;
      });
      // Clear last selected if all filtered items are deselected
      if (itemsWithIds.length > 0 && lastSelectedImageIdRef.current) {
        const lastSelectedInFiltered = itemsWithIds.some(
          (item) => item.imageId === lastSelectedImageIdRef.current,
        );
        if (lastSelectedInFiltered) {
          lastSelectedImageIdRef.current = null;
        }
      }
    } else {
      // Select all filtered items
      setSelectedImageIds((prev) => {
        const newSet = new Set(prev);
        filteredImageIds.forEach((id) => newSet.add(id));
        return newSet;
      });
      // Set last selected to the last item in the filtered list
      if (itemsWithIds.length > 0) {
        const lastItem = itemsWithIds[itemsWithIds.length - 1];
        if (lastItem?.imageId) {
          lastSelectedImageIdRef.current = lastItem.imageId;
        }
      }
    }
  }, [allItemsSelected, filteredItems]);

  const columnsWithHeaderCheckbox = useMemo(
    () => getColumns(allItemsSelected, handleToggleAll, selectedImageIds, toggleItemSelection, t),
    [allItemsSelected, handleToggleAll, selectedImageIds, toggleItemSelection, t],
  );

  return (
    <OverlayLoader visible={isPendingGetImages}>
      <Stack spacing={1}>
        <Stack alignItems="center" direction="row" spacing={0.5}>
          <TextBox
            placeholder={
              isMultiselectActive
                ? t('imageList.selectedItems', {
                    ns: 'cloudProcessing',
                    count: selectedImageIds.size,
                  })
                : t('search', { ns: 'common' })
            }
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
            }}
          />
          <Icon
            disabled={!isMultiselectActive}
            icon={['fad', 'pencil']}
            title={t('gcpPage.bulkUpdateImagesDialog.title', { ns: 'cloudProcessing' })}
            onClick={handleBulkUpdate}
            onHoverStyle={{ icon: ['fas', 'pencil'], opacity: 0.7 }}
          />
          <FileSelectionIcon
            accept=".jpg,.jpeg"
            color={theme.color.gray.dark}
            disabled={isGeodataProcessing}
            fixedWidth={true}
            icon={['fad', 'images']}
            multiple={true}
            title={t('aligner.addImages', { ns: 'cloudProcessing' })}
            onFilesSelected={async (files) => {
              for (const file of files) {
                await uploadImage(file);
              }
            }}
            onHoverStyle={{ icon: ['fas', 'images'] }}
          />
          <FileSelectionIcon
            accept=".jpg,.jpeg"
            color={theme.color.gray.dark}
            fixedWidth={true}
            icon={['fad', 'link']}
            multiple={true}
            title={t('aligner.linkLocalImages', { ns: 'cloudProcessing' })}
            onFilesSelected={(files) => linkLocalImages(files)}
            onHoverStyle={{ icon: ['fas', 'link'] }}
          />
          <Icon
            color={theme.color.gray.dark}
            fixedWidth={true}
            icon={['fad', 'refresh']}
            title={t('refreshList', { ns: 'common' })}
            onClick={() => refetchImages()}
            onHoverStyle={{ icon: ['fas', 'refresh'] }}
          />
        </Stack>

        {renderNoImagesPanel ? (
          <NoImagesPanel />
        ) : (
          <DataTable
            columns={columnsWithHeaderCheckbox}
            disableTextSelect={isMultiselectActive}
            fixedLayout={true}
            getRowKey={(item) => item.imageId ?? String(item.displayName ?? '')}
            highlightRow={highlightRow}
            items={filteredItems}
            pageSize={pageSize}
            tableStyle={tableStyle}
            onRowClicked={onRowClicked}
            onSortingChanged={setSortedItems}
          />
        )}
      </Stack>
      {bulkUpdateDialog.render(
        geodata && (
          <BulkUpdateImagesDialog
            geodataId={geodata.id}
            items={selectedItems}
            photoCount={geodata.photoCount}
            onClose={() => bulkUpdateDialog.hide()}
          />
        ),
      )}
    </OverlayLoader>
  );
}
